/**
 * Measures the real byte size of a DWT-generated upload blob (the `resultURL` from
 * `GenerateURLForUploadData`). DWT's own size estimate (`GetSelectedImagesSize`) is measured
 * against the buffered images, not the file actually generated here — for scans acquired via
 * the physical scanner the two agree closely enough, but a file loaded via "Open" keeps its
 * original resolution/quality and can encode to something much larger than the estimate
 * predicted. Returns undefined (instead of throwing) if the size can't be determined, so a
 * transient fetch failure doesn't block a release that would otherwise succeed — the server's
 * own size limit remains as a backstop in that case. Bounded by a timeout so a slow/stuck fetch
 * can't turn a previously-instant release into an indefinite hang — this check must never be
 * able to block a release for longer than it would take the upload itself to just fail.
 */
export const measureGeneratedFileSize = async (resultURL: string, timeoutMs = 10000): Promise<number | undefined> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(resultURL, { signal: controller.signal });
    const blob = await response.blob();
    return blob.size;
  } catch (e) {
    console.log('Unable to measure generated upload size', e);
    return undefined;
  } finally {
    clearTimeout(timeout);
  }
};
